import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminLogin, adminMe, fetchEvents, fetchSettings, fetchPackages, updateEvent, updateSettings, updatePackage } from "../lib/api";

const Field = ({ label, value, onChange, textarea, testid }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-[0.2em] text-slate-400">{label}</span>
    {textarea ? (
      <textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={4}
        className="mt-1.5 w-full rounded-xl border border-slate-900/10 bg-white px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00B4D8]" />
    ) : (
      <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full rounded-xl border border-slate-900/10 bg-white px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00B4D8]" />
    )}
  </label>
);

const linesToLineup = (s) => s.split("\n").filter(Boolean).map((l) => {
  const [name, role = ""] = l.split("|").map((x) => x.trim());
  return { name, role };
});
const lineupToLines = (a) => (a || []).map((x) => `${x.name} | ${x.role}`).join("\n");
const linesToProgram = (s) => s.split("\n").filter(Boolean).map((l) => {
  const [time, item = ""] = l.split("|").map((x) => x.trim());
  return { time, item };
});
const programToLines = (a) => (a || []).map((x) => `${x.time} | ${x.item}`).join("\n");

const EventEditor = ({ ev, onSaved }) => {
  const [form, setForm] = useState({ ...ev, lineupText: lineupToLines(ev.lineup), programText: programToLines(ev.program), programRuText: programToLines(ev.program_ru), galleryText: (ev.gallery || []).join("\n") });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try {
      await updateEvent(ev.slug, {
        title: form.title, tagline: form.tagline, tagline_ru: form.tagline_ru,
        date: form.date, date_ru: form.date_ru, time: form.time, time_ru: form.time_ru,
        venue: form.venue, venue_address: form.venue_address, map_query: form.map_query,
        dress_code: form.dress_code, dress_code_ru: form.dress_code_ru,
        description: form.description, description_ru: form.description_ru,
        poster: form.poster, hero_image: form.hero_image,
        lineup: linesToLineup(form.lineupText), program: linesToProgram(form.programText),
        program_ru: linesToProgram(form.programRuText),
        gallery: form.galleryText.split("\n").filter(Boolean),
      });
      toast.success(`${form.title} saved`);
      onSaved();
    } catch (e) {
      toast.error("Save failed. Session may have expired — log in again.");
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 border border-slate-900/10 rounded-2xl p-6 bg-[#FDFBF7]">
      <Field label="Title" value={form.title} onChange={set("title")} testid={`admin-${ev.slug}-title`} />
      <Field label="Tagline (EN)" value={form.tagline} onChange={set("tagline")} testid={`admin-${ev.slug}-tagline`} />
      <Field label="Tagline (RU)" value={form.tagline_ru} onChange={set("tagline_ru")} testid={`admin-${ev.slug}-tagline-ru`} />
      <Field label="Date (EN)" value={form.date} onChange={set("date")} testid={`admin-${ev.slug}-date`} />
      <Field label="Date (RU)" value={form.date_ru} onChange={set("date_ru")} testid={`admin-${ev.slug}-date-ru`} />
      <Field label="Time (EN)" value={form.time} onChange={set("time")} testid={`admin-${ev.slug}-time`} />
      <Field label="Time (RU)" value={form.time_ru} onChange={set("time_ru")} testid={`admin-${ev.slug}-time-ru`} />
      <Field label="Venue" value={form.venue} onChange={set("venue")} testid={`admin-${ev.slug}-venue`} />
      <Field label="Venue address" value={form.venue_address} onChange={set("venue_address")} testid={`admin-${ev.slug}-address`} />
      <Field label="Map query (Google Maps)" value={form.map_query} onChange={set("map_query")} testid={`admin-${ev.slug}-map`} />
      <Field label="Dress code (EN)" value={form.dress_code} onChange={set("dress_code")} testid={`admin-${ev.slug}-dresscode`} />
      <Field label="Dress code (RU)" value={form.dress_code_ru} onChange={set("dress_code_ru")} testid={`admin-${ev.slug}-dresscode-ru`} />
      <div className="md:col-span-2">
        <Field label="Description (EN)" value={form.description} onChange={set("description")} textarea testid={`admin-${ev.slug}-description`} />
      </div>
      <div className="md:col-span-2">
        <Field label="Description (RU)" value={form.description_ru} onChange={set("description_ru")} textarea testid={`admin-${ev.slug}-description-ru`} />
      </div>
      <Field label="Poster image URL" value={form.poster} onChange={set("poster")} testid={`admin-${ev.slug}-poster`} />
      <Field label="Hero image URL" value={form.hero_image} onChange={set("hero_image")} testid={`admin-${ev.slug}-hero`} />
      <Field label="Lineup (one per line: Name | Role)" value={form.lineupText} onChange={set("lineupText")} textarea testid={`admin-${ev.slug}-lineup`} />
      <Field label="Program EN (one per line: Time | Item)" value={form.programText} onChange={set("programText")} textarea testid={`admin-${ev.slug}-program`} />
      <Field label="Program RU (one per line: Time | Item)" value={form.programRuText} onChange={set("programRuText")} textarea testid={`admin-${ev.slug}-program-ru`} />
      <div className="md:col-span-2">
        <Field label="Gallery image URLs (one per line)" value={form.galleryText} onChange={set("galleryText")} textarea testid={`admin-${ev.slug}-gallery`} />
      </div>
      <button data-testid={`admin-${ev.slug}-save`} onClick={save}
        className="md:col-span-2 rounded-full bg-[#00B4D8] hover:bg-[#0096b4] text-white text-xs uppercase tracking-[0.2em] px-8 py-4 transition-colors duration-300 w-fit">
        Save {form.title}
      </button>
    </div>
  );
};

const PackageEditor = ({ pkg, onSaved }) => {
  const [form, setForm] = useState({ ...pkg, includesText: (pkg.includes || []).join("\n"), includesRuText: (pkg.includes_ru || []).join("\n") });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      await updatePackage(pkg.slug, {
        name: form.name, subtitle: form.subtitle, subtitle_ru: form.subtitle_ru,
        price: Number(form.price), quantity: Number(form.quantity),
        includes: form.includesText.split("\n").filter(Boolean),
        includes_ru: form.includesRuText.split("\n").filter(Boolean),
      });
      toast.success(`${form.name} saved`);
      onSaved();
    } catch {
      toast.error("Save failed. Session may have expired — log in again.");
    }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 border border-slate-900/10 rounded-2xl p-6 bg-[#FDFBF7] mb-8">
      <Field label="Name" value={form.name} onChange={set("name")} testid={`admin-pkg-${pkg.slug}-name`} />
      <Field label="Price (USD)" value={String(form.price)} onChange={set("price")} testid={`admin-pkg-${pkg.slug}-price`} />
      <Field label="Subtitle (EN)" value={form.subtitle} onChange={set("subtitle")} testid={`admin-pkg-${pkg.slug}-subtitle`} />
      <Field label="Subtitle (RU)" value={form.subtitle_ru} onChange={set("subtitle_ru")} testid={`admin-pkg-${pkg.slug}-subtitle-ru`} />
      <Field label="Quantity" value={String(form.quantity)} onChange={set("quantity")} testid={`admin-pkg-${pkg.slug}-quantity`} />
      <div />
      <Field label="Includes EN (one per line)" value={form.includesText} onChange={set("includesText")} textarea testid={`admin-pkg-${pkg.slug}-includes`} />
      <Field label="Includes RU (one per line)" value={form.includesRuText} onChange={set("includesRuText")} textarea testid={`admin-pkg-${pkg.slug}-includes-ru`} />
      <button data-testid={`admin-pkg-${pkg.slug}-save`} onClick={save}
        className="md:col-span-2 rounded-full bg-[#00B4D8] hover:bg-[#0096b4] text-white text-xs uppercase tracking-[0.2em] px-8 py-4 transition-colors duration-300 w-fit">
        Save {form.name}
      </button>
    </div>
  );
};

const SettingsEditor = ({ settings, onSaved }) => {
  const [form, setForm] = useState({ ...settings });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      const { key, ...data } = form;
      await updateSettings(data);
      toast.success("Settings saved");
      onSaved();
    } catch {
      toast.error("Save failed. Session may have expired — log in again.");
    }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 border border-slate-900/10 rounded-2xl p-6 bg-[#FDFBF7]">
      <Field label="Festival name" value={form.festival_name} onChange={set("festival_name")} testid="admin-settings-name" />
      <Field label="Tagline (EN)" value={form.tagline} onChange={set("tagline")} testid="admin-settings-tagline" />
      <Field label="Tagline (RU)" value={form.tagline_ru} onChange={set("tagline_ru")} testid="admin-settings-tagline-ru" />
      <div className="md:col-span-2">
        <Field label="Description (EN)" value={form.description} onChange={set("description")} textarea testid="admin-settings-description" />
      </div>
      <div className="md:col-span-2">
        <Field label="Description (RU)" value={form.description_ru} onChange={set("description_ru")} textarea testid="admin-settings-description-ru" />
      </div>
      <Field label="Hero video URL (mp4)" value={form.hero_video_url} onChange={set("hero_video_url")} testid="admin-settings-video" />
      <Field label="Hero poster / fallback image URL" value={form.hero_poster_url} onChange={set("hero_poster_url")} testid="admin-settings-poster" />
      <div className="md:col-span-2">
        <Field label="Ticket URL" value={form.ticket_url} onChange={set("ticket_url")} testid="admin-settings-ticket" />
      </div>
      <Field label="Instagram" value={form.instagram} onChange={set("instagram")} testid="admin-settings-instagram" />
      <Field label="Facebook" value={form.facebook} onChange={set("facebook")} testid="admin-settings-facebook" />
      <Field label="Telegram" value={form.telegram} onChange={set("telegram")} testid="admin-settings-telegram" />
      <Field label="Contact email" value={form.contact_email} onChange={set("contact_email")} testid="admin-settings-email" />
      <button data-testid="admin-settings-save" onClick={save}
        className="md:col-span-2 rounded-full bg-[#00B4D8] hover:bg-[#0096b4] text-white text-xs uppercase tracking-[0.2em] px-8 py-4 transition-colors duration-300 w-fit">
        Save Settings
      </button>
    </div>
  );
};

export default function AdminPage() {
  const [authed, setAuthed] = useState(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [events, setEvents] = useState([]);
  const [packages, setPackages] = useState([]);
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState("settings");

  const load = () => {
    fetchEvents().then(setEvents).catch(() => {});
    fetchSettings().then(setSettings).catch(() => {});
    fetchPackages().then(setPackages).catch(() => {});
  };

  useEffect(() => {
    if (!localStorage.getItem("smf_token")) { setAuthed(false); return; }
    adminMe().then(() => { setAuthed(true); load(); }).catch(() => setAuthed(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { token } = await adminLogin(password);
      localStorage.setItem("smf_token", token);
      setAuthed(true);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed");
    }
  };

  if (authed === null) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 rounded-full border-2 border-[#00B4D8] border-t-transparent animate-spin" /></div>;

  if (!authed)
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-[#FDFBF7]">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm border border-slate-900/10 rounded-2xl p-8 bg-white shadow-[0_8px_32px_rgba(0,0,0,0.06)]">
          <h1 className="font-serif text-3xl text-slate-900 mb-1">Admin</h1>
          <p className="text-slate-400 text-sm mb-7">St. Martin Music Festival CMS</p>
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full rounded-xl border border-slate-900/10 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
          />
          {error && <p data-testid="admin-login-error" className="text-red-500 text-sm mt-3">{error}</p>}
          <button data-testid="admin-login-submit" type="submit"
            className="mt-5 w-full rounded-full bg-[#00B4D8] hover:bg-[#0096b4] text-white text-xs uppercase tracking-[0.2em] py-4 transition-colors duration-300">
            Log in
          </button>
        </form>
      </div>
    );

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-white pt-28 pb-24">
      <div className="max-w-5xl mx-auto px-5 md:px-10">
        <div className="flex items-center justify-between mb-10">
          <h1 className="font-serif text-4xl text-slate-900">Content Admin</h1>
          <button data-testid="admin-logout" onClick={() => { localStorage.removeItem("smf_token"); setAuthed(false); }}
            className="text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-slate-900 transition-colors duration-300">Log out</button>
        </div>
        <div className="flex flex-wrap gap-3 mb-10">
          {["settings", "packages", ...events.map((e) => e.slug)].map((t) => (
            <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)}
              className={`rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.15em] border transition-colors duration-300 ${tab === t ? "bg-slate-900 text-white border-slate-900" : "border-slate-900/15 text-slate-500 hover:border-slate-900"}`}>
              {t === "settings" ? "Site Settings" : t === "packages" ? "Packages" : events.find((e) => e.slug === t)?.title || t}
            </button>
          ))}
        </div>
        {tab === "settings" && settings && <SettingsEditor settings={settings} onSaved={load} />}
        {tab === "packages" && packages.map((p) => <PackageEditor key={p.slug} pkg={p} onSaved={load} />)}
        {events.map((ev) => tab === ev.slug && <EventEditor key={ev.slug} ev={ev} onSaved={load} />)}
      </div>
    </div>
  );
}
